/**
 * CollectionsHomeTab - Home tab showing Collections as cards
 *
 * Atomic level: Organism
 * Displays collections in a card grid.
 *
 * Phase 2 of Collection-First Conduct Records redesign:
 * - Removed table view in favor of card grid
 * - Removed ViewsSection (views are now collection-scoped in detail page)
 * - Cards sorted by conduct score (highest first, N/A at end)
 *
 * Following GO UI Design System patterns
 */

import React from 'react';
import { Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../ui/button';
import { CollectionCardGrid } from './components/CollectionCardGrid';
import { useCollections } from '../../../hooks/useRecords';
import { useAccess, Capability } from '../../../hooks/useAccess';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { getActingOrg } from '../../../utils/actingOrg';
import type { Collection } from '../../../services/types';

export const CollectionsHomeTab: React.FC = () => {
  const navigate = useNavigate();
  // Monitor setup is org-admin work (confirmed with Laurence): only an
  // organization admin — or a platform admin — may add a source. Gate on the
  // RBAC org-admin capability, not the legacy `role === 'admin'` string (an RBAC
  // org admin has legacy role "user", so isAdmin() wrongly hid this).
  //
  // Match what creating a monitor actually needs end-to-end: POST /import-jobs
  // is gated on (FIRM_MANAGE || PLATFORM_MANAGE) at the router AND
  // MONITORING_CONTRIBUTE on create. A real org admin (FIRM_ADMIN) holds both;
  // a home-org platform admin holds PLATFORM_MANAGE but not MONITORING_CONTRIBUTE,
  // so without this composite it would see the wizard and 403 on Save. Acting-as
  // (god mode) synthesises MONITORING_CONTRIBUTE, so a platform admin operating
  // over a client org still qualifies.
  const { access, can, isLoading: accessLoading } = useAccess();
  const canSetUpMonitor =
    can(Capability.MonitoringContribute)
    && (can(Capability.FirmManage) || can(Capability.PlatformManage));

  // isPending, not isLoading: a query paused offline is still waiting, not empty.
  const { data: collectionsData, isPending, isError } = useCollections();
  const collections = collectionsData?.collections || [];
  const { data: currentUser, isPending: currentUserPending, fetchStatus: currentUserFetch } =
    useCurrentUser();
  // Pending and not idle: fetching or paused offline, but not disabled.
  const currentUserWaiting = currentUserPending && currentUserFetch !== 'idle';
  // The empty grid says something different by role and names the admin, so it
  // waits for the access profile, and for /me where it names someone.
  const gridLoading =
    isPending
    || accessLoading
    || (!canSetUpMonitor && currentUserWaiting && collections.length === 0);
  // Without the access profile the role is unknown, so neither empty state is safe to show.
  const loadFailed = (isError && !collectionsData) || (!accessLoading && !access);

  // /me carries no organization name, so an admin's own organization is named
  // generically; a platform admin acting as one sees its name.
  const subtitle = canSetUpMonitor
    ? `The monitors in ${getActingOrg()?.name ?? 'your organization'}.`
    : 'The monitors shared with you.';

  const handleCollectionClick = (collection: Collection) => {
    navigate(`/v4/records/${collection.id}`);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-foreground">Monitor Conduct</h1>
        {access && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>

      {/* While the grid is empty its own button is the only way in. */}
      {canSetUpMonitor && collections.length > 0 && (
        <div className="flex items-center justify-end">
          <Button asChild variant="outline" className="gap-2">
            <Link to="/v4/monitor/new">
              <Plus className="h-4 w-4" />
              New monitor
            </Link>
          </Button>
        </div>
      )}

      {loadFailed ? (
        <p role="alert" className="text-sm text-muted-foreground">
          We couldn't load your monitors. Refresh the page to try again.
        </p>
      ) : (
        <CollectionCardGrid
          collections={collections}
          onCollectionClick={handleCollectionClick}
          isLoading={gridLoading}
          canSetUpMonitor={canSetUpMonitor}
          adminName={currentUser?.invited_by_name}
        />
      )}
    </div>
  );
};
